import { Injectable, OnInit } from '@angular/core';
import { Platform } from '@ionic/angular';
import { ConfigService } from '../config/config.service';
import { WebservicesService } from '../webservices/webservices.service';
import { UserSessionService } from '../user-session/user-session.service';
import { AppstorageTsService } from '../appstorage/appstorage.ts.service';
import { LoginService } from '../login/login.service';
import { GalleryService } from '../gallery/gallery.service';
import { Navigator } from 'src/app/components/menu/navigator';
import { EventsService } from '../event/event.service';
import { flagChecker } from '../codeUtils';

//Apple
import {
  SignInWithApple,
  SignInWithAppleResponse,
  SignInWithAppleOptions,
} from '@capacitor-community/apple-sign-in';

//Facebook
import { FacebookLogin, FacebookLoginResponse } from '@capacitor-community/facebook-login';

//Google
import { GoogleAuth, User } from '@codetrix-studio/capacitor-google-auth';
import { AppsflyerService } from '../marketing/appsflyer.service';
import { TrackConsentService } from '../tracking-consent/track-consent.service';

@Injectable({
  providedIn: 'root'
})
export class SocialAuthService implements OnInit {

  public clientApp: any;
  public facebookAppID: string;
  public googleClientID: string;
  public isPlayerLoggedInAsGuest: boolean = false;
  public customerID: string | number;
  public sessionToken: string;


  constructor(
    private readonly appStorage: AppstorageTsService,
    private readonly config: ConfigService,
    private readonly galleryService: GalleryService,
    private readonly loginService: LoginService,
    private readonly navigator: Navigator,
    private readonly platform: Platform,
    private readonly eventsService: EventsService,
    private readonly userSession: UserSessionService,
    private readonly webservice: WebservicesService,
    private appsFlyerService: AppsflyerService,
    private trackConsentService: TrackConsentService
  ) {
    this.getSocialConfigs();
    this.clientApp = this.config?.getClientApp();
    this.getCustomerId();
  }

  ngOnInit(): void {
  }

  private getSocialConfigs(): void {
    const config = this.config.getConfig();
    this.facebookAppID = config?.AUTH_FB_APP_ID;
    if (this.platform.is("capacitor") && this.platform.is("ios")) {
      this.googleClientID = config?.AUTH_GOOGLE_IOS_CLIENT_ID;
    } else if (this.platform.is("capacitor") && this.platform.is("android")) {
      this.googleClientID = config?.AUTH_GOOGLE_ANDROID_CLIENT_ID;
    } else {
      this.googleClientID = config?.AUTH_GOOGLE_CLIENT_ID;
    }
  }

  public socialAuthInit(): void {
    this.fbInit();
    this.googleInit();
  }

  //Apple Start

  async appleLogin(isguest?): Promise<any> {
    this.isPlayerLoggedInAsGuest = isguest;
    let options: SignInWithAppleOptions = {
      clientId: 'com.ati.b2bsocial.stardust.client',
      redirectURI: 'https://www.stardustsocialcasino.com',
      scopes: 'email name'
    };

    SignInWithApple.authorize(options)
      .then((result: SignInWithAppleResponse) => {
        const obj = {
          access_token: result?.response.identityToken,
          user_id: result?.response.user,
          force_logout: true,
          system_id: '15'
        }

        return this.thirdPartyLogin(obj).subscribe((response) => {
          if (response && response?.result === 1) {
            const payload = response?.payload;
            this.guestToSocialUpgradeActions();
            return this.continueSocialLogin(payload);
          }

          return console.error('ThirdPartyAuth:Apple:Error: ', response);
        }, error => {
          console.error('ThirdPartyAuth:Apple:Error: ', error);
          this.handleError(error, 'Apple');
        });
      })
      .catch(error => {
        this.handleError(error, 'Apple');
      });
  }
  //Apple End

  //Facebook Start

  //!Important   //Note for Facebook login to work you need https in localhost.
  //!Important   //Note for Facebook login to work you need https in localhost.
  //!Important   //Note for Facebook login to work you need https in localhost.


  private async fbInit(): Promise<any> {
    await this.platform.ready().then(() => {
      FacebookLogin.initialize({ appId: this.facebookAppID });
    });
  }

  async fbLogin(isguest?): Promise<FacebookLoginResponse> {
    if (this.platform.is("ios") && this.platform.is("capacitor")) {
      const trackingStatus = await this.trackConsentService.getTrackingStatus();
      console.log("trackingStatus", trackingStatus);

      if (trackingStatus == "Authorized") {
        setTimeout(() => {
          return this.processFBLogin(isguest);
        }, 1500);
      }
    } else {
      return this.processFBLogin(isguest);
    }
  }

  async processFBLogin(isguest?): Promise<FacebookLoginResponse> {
    this.isPlayerLoggedInAsGuest = isguest;
    const FACEBOOK_PERMISSIONS = ['public_profile', 'email'];
    const result = await FacebookLogin.login({ permissions: FACEBOOK_PERMISSIONS });

    const profile = await FacebookLogin.getProfile<{
      email: string;
      id: number;
    }>({ fields: ['email', 'id'] });

    const obj = {
      access_token: result?.accessToken?.token,
      user_id: profile?.id,
      system_id: '3'
    }

    return this.thirdPartyLogin(obj).subscribe((response) => {
      if (response && response?.result === 1) {
        const payload = response?.payload;
        this.guestToSocialUpgradeActions();
        return this.continueSocialLogin(payload);
      }

      return console.error('ThirdPartyAuth:Facebook:Error: ', response);
    }, error => {
      console.error('ThirdPartyAuth:Facebook:Error: ', error);
      this.handleError(error, 'Facebook');
    });

  }

  async fbLogout(): Promise<any> {
    const result = await FacebookLogin.logout();
    console.log('fbLogout: ', result);
  }

  async getCurrentAccessToken(): Promise<boolean> {
    const result = await FacebookLogin.getCurrentAccessToken().catch(
      () => undefined,
    );
    return !(result === undefined || !result.accessToken);
  }

  //Facebook End

  //Google Start
  private async googleInit() {
    await this.platform.ready().then(() => {
      GoogleAuth.initialize({
        clientId: this.googleClientID,
        scopes: ['profile', 'email'],
        grantOfflineAccess: true,
      });
    });
  }

  public async googleLogin(isguest?) {
    this.isPlayerLoggedInAsGuest = isguest;
    const user: User = await GoogleAuth.signIn();
    const auth: any = user?.authentication;
    const obj = {
      access_token: auth?.idToken,
      system_id: '14'
    }

    return this.thirdPartyLogin(obj).subscribe((response) => {
      if (response && response?.result === 1) {
        const payload = response?.payload;
        this.guestToSocialUpgradeActions();
        return this.continueSocialLogin(payload, user);

      }

      return console.error('ThirdPartyAuth:Google:Error: ', response);
    }, error => {
      console.error('ThirdPartyAuth:Google:Error: ', error);
      this.handleError(error, 'Google');
    });
  }

  //Google End

  public continueSocialLogin(serviceRes: any, profileData?: any): any {
    //only store local information if server sends as it seems authentication/third-party-system-connect don't return anything to client
    if (serviceRes) this.loginService.setUserLogin(serviceRes);
    const platformId = serviceRes?.platformID;
    const sessionToken = serviceRes?.sessionToken;

    this.navigator.toLobby();
    if (sessionToken) this.userSession.setSessionToken(sessionToken);
    if (platformId) this.appStorage.setPlayerPlatformID(platformId);
    this.appStorage.setPlayerAsGuest(false);
    this.appStorage.setPlayerAsSocial(true);
    this.loginService.setLoginResponseInStorage().then(() => {
      this.galleryService.getGameList()
    });
  }

  private handleError(error: any, type?: string): any {

    const errorCode = error?.error_code;
    if (errorCode == "EMAIL_ALREADY_EXISTS") return this.socialAuthErrorPopup(error, type);
    if (errorCode == "ALREADY_HAS_ACCOUNT") return this.socialAuthErrorPopup(error, type);
    if (errorCode == "CUSTOMER_EXCLUDED_BY_AUTHORITY") return this.eventsService.publish("events:social:auth:error", error);
    if (errorCode == "CUSTOMER_WRONG_STATUS") return this.eventsService.publish("events:social:auth:error", error);
    return this.navigator.navigateTo('SignupOptionsStepTwoComponent', { customModalClass: 'crown-modal', multipleModals: true });
  }

  private socialAuthErrorPopup(error: any, type?: string): any {
    this.navigator.dismissAll();
    const data = {
      error: error,
      type: type
    }
    return this.navigator.navigateTo('SignupRedirectComponent', { customModalClass: 'crown-modal', multipleModals: true }, { data: data })
  }

  //Request
  protected socialOveride: boolean = false;
  public thirdPartyLogin(obj?: any): any {

    this.eventsService.subscribe("event:social:guest:already", (flag: boolean) => {
      this.socialOveride = flag
    });

    let trackingData = this.appsFlyerService.getTrackingIDs();
    obj.deviceDetails = trackingData.deviceDetails;
    obj.externalSource = trackingData.externalSource;
    if (!obj.deviceDetails) delete obj.deviceDetails;
    if (!obj.externalSource) delete obj.externalSource;

    if (this.isPlayerLoggedInAsGuest && !this.socialOveride && this.customerID) {
      obj.clientapp = this.clientApp,
        obj.customer_id = this.customerID,
        obj.session_token = this.userSession.getSessionToken()
      return this.webservice.post('authentication/third-party-system-connect', obj)
    } else {
      obj.clientapp = this.clientApp;
      return this.webservice.post('authentication/third-party-login', obj);
    }
  }

  private guestToSocialUpgradeActions(): any {
    this.appStorage.setPlayerAsGuest(false);
    this.appStorage.removePlayerPlatformID();
    this.eventsService.destroy("event:social:guest:already");
  }

  private getCustomerId(): any {
    return this.userSession.getCustomer().subscribe((customer) => {
      if (customer) {
        this.customerID = customer.customerId;
        this.sessionToken = this.userSession.getSessionToken();
      }
    });
  }

  //Feature Flags

  get playAsGuestEnabled(): boolean {
    const config = this.config.getConfig();
    const isApps = this.platform.is('capacitor') || this.platform.is('cordova');

    return flagChecker(config?.FEATURE_GUEST_LOGIN_ENABLED) ||
      (flagChecker(config?.FEATURE_GUEST_LOGIN_ENABLED_APPS) && isApps);
  }

  get appleAuthEnabled(): boolean {
    const config = this.config.getConfig();
    const isIosApp = this.platform.is('capacitor') && this.platform.is('ios');

    return flagChecker(config?.FEATURE_APPLE_AUTH_ENABLED_IOS) && isIosApp;
  }

  get fbAuthEnabled(): boolean {
    const config = this.config.getConfig();
    return flagChecker(config?.FEATURE_FB_AUTH_ENABLED);
  }

  get googleAuthEnabled(): boolean {
    const config = this.config.getConfig();
    return flagChecker(config?.FEATURE_GOOGLE_AUTH_ENABLED);
  }

}